/* 
GLOBAL ADVANCED DATA V1.0
DESAROLLADORES: 
  KEVIN DAVID SILVA
  GERSON DIAZ
  ISRAEL GONZALEZ
*/

import { useState, useMemo } from "react";
import {
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  Box,
  Typography,
  Chip,
  InputAdornment,
  Button,
  TablePagination,
} from "@mui/material";
import {
  Search as SearchIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";
import * as XLSX from "xlsx";
import { useGetallusersQuery } from "../Pages/LoginPage/store/loginSlice";

const ListUser = () => {
  // Paginación (puede ser en el servidor o cliente)
  const [skip, setSkip] = useState(0);
  const [limit, setLimit] = useState(10);

  // Ajusta según tu backend: si retorna { totalCount, results }, desestructura
  const { data, isLoading } = useGetallusersQuery({ skip, limit });

  // Si tu backend retorna un array simple, haz esto:
  const usersAPI = data || [];


  const totalCount = usersAPI.length;

  // Filtros
  const [selectedRole, setSelectedRole] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  // Roles y países
  const roles = useMemo(() => {
    const uniqueRoles = new Set(usersAPI.map((user) => user.role?.name || ""));
    return [...uniqueRoles];
  }, [usersAPI]);

  const countries = useMemo(() => {
    const uniqueCountries = new Set(usersAPI.map((user) => user.country || ""));
    return [...uniqueCountries];
  }, [usersAPI]);

  // Filtrado
  const filteredUsers = useMemo(() => {
    return usersAPI.filter((user) => {
      const matchesRole =
        selectedRole === "" || user.role?.name === selectedRole;
      const matchesCountry =
        selectedCountry === "" || user.country === selectedCountry;
      const matchesSearch =
        searchTerm === "" ||
        user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesRole && matchesCountry && matchesSearch;
    });
  }, [usersAPI, selectedRole, selectedCountry, searchTerm]);

  // Color de chip según rol
  const getPlanBadgeColor = (role) => {
    switch (role) {
      case "admin":
        return "primary";
      case "user":
        return "success";
      default:
        return "default";
    }
  };

  // Exportar a Excel
  // -> Cada fila en Excel representará un user+plan
  const exportToXLSX = () => {
    // Construimos un array 'flattened'
    const dataForExcel = [];

    filteredUsers.forEach((user) => {
      // Si tiene planes, generamos una fila por plan
      if (user.payment_plans?.length) {
        user.payment_plans.forEach((plan) => {
          dataForExcel.push({
            "ID Usuario": user.id,
            Nombre: user.name,
            Apellido: user.lastname,
            Email: user.email,
            País: user.country,
            Rol: user.role?.name,
            Monto:
              plan.serfinsa_response &&
              plan.serfinsa_response.cliente_trans_monto
                ? `$${(
                    parseInt(plan.serfinsa_response.cliente_trans_monto) / 100
                  ).toFixed(2)}`
                : plan.amount || "-",
            "Método Pago": plan.payment_method,
            Estado: plan.status,
            "Transacción ID": plan.transaction_id,
            "Fecha Expiración": plan.expiration_date
              ? new Date(plan.expiration_date).toLocaleDateString("es-ES", {
                  year: "numeric",
                  month: "2-digit",
                  day: "2-digit",
                  hour: "2-digit",
                  minute: "2-digit",
                })
              : "",
            "Creado en": plan.created_at
              ? new Date(plan.created_at).toLocaleDateString("es-ES", {
                  year: "numeric",
                  month: "2-digit",
                  day: "2-digit",
                  hour: "2-digit",
                  minute: "2-digit",
                })
              : "",
            "Actualizado en": plan.updated_at
              ? new Date(plan.updated_at).toLocaleDateString("es-ES", {
                  year: "numeric",
                  month: "2-digit",
                  day: "2-digit",
                  hour: "2-digit",
                  minute: "2-digit",
                })
              : "",
          });
        });
      } else {
        dataForExcel.push({
          "ID Usuario": user.id,
          Nombre: user.name,
          Apellido: user.lastname,
          Email: user.email,
          País: user.country,
          Rol: user.role?.name,
          Monto: "",
          "Método Pago": "",
          Estado: "",
          "Transacción ID": "",
          "Fecha Expiración": "",
          "Creado en": "",
          "Actualizado en": "",
        });
      }
    });

    const worksheet = XLSX.utils.json_to_sheet(dataForExcel);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Usuarios");
    XLSX.writeFile(workbook, "usuarios.xlsx");
  };

  // Paginación en el cliente o servidor
  // -> Ajusta si tu backend no usa skip/limit
  const handleChangePage = (event, newPage) => {
    setSkip(newPage * limit);
  };

  const handleChangeRowsPerPage = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    setLimit(newLimit);
    setSkip(0);
  };

  return (
    <Box sx={{ mx: "auto", px: 2, py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Lista de Usuarios y Planes
      </Typography>

      {/* Filtros */}
      <Paper sx={{ p: 2, mb: 4 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
          }}
        >
          <Select
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value)}
            displayEmpty
            fullWidth
          >
            <MenuItem value="">Filtrar por Rol</MenuItem>
            {roles.map((role) => (
              <MenuItem key={role} value={role}>
                {role}
              </MenuItem>
            ))}
          </Select>

          <Select
            value={selectedCountry}
            onChange={(e) => setSelectedCountry(e.target.value)}
            displayEmpty
            fullWidth
          >
            <MenuItem value="">Filtrar por País</MenuItem>
            {countries.map((country) => (
              <MenuItem key={country} value={country}>
                {country}
              </MenuItem>
            ))}
          </Select>
        </Box>

        <Box sx={{ position: "relative", flexGrow: 1, mt: 2 }}>
          <TextField
            variant="outlined"
            placeholder="Buscar por nombre o email"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "gray" }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Box sx={{ mt: 2, textAlign: "right" }}>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<DownloadIcon />}
            onClick={exportToXLSX}
          >
            Exportar a Excel
          </Button>
        </Box>
      </Paper>

      {/* Tabla */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {/* Columnas del usuario */}
              <TableCell>Nombre</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>País</TableCell>
              <TableCell>Rol</TableCell>
              {/* Columnas del plan */}
              <TableCell>Monto</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>ID Transacción</TableCell>
              <TableCell>Expiración</TableCell>
              <TableCell>Creado</TableCell>
              <TableCell>Actualizado</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={11} align="center">
                  Cargando...
                </TableCell>
              </TableRow>
            ) : filteredUsers.length > 0 ? (
              filteredUsers.map((user) => {
                // Si el usuario tiene planes
                if (user.payment_plans?.length) {
                  return user.payment_plans.map((plan) => (
                    <TableRow key={`${user.id}-${plan.id}`}>
                      <TableCell>
                        {user.name} {user.lastname}
                      </TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{user.country}</TableCell>
                      <TableCell>
                        <Chip
                          label={user.role?.name}
                          color={getPlanBadgeColor(user.role?.name)}
                        />
                      </TableCell>

                      {/* Plan */}
                      <TableCell>
                        {plan.serfinsa_response &&
                        plan.serfinsa_response.cliente_trans_monto
                          ? `$${(
                              parseInt(
                                plan.serfinsa_response.cliente_trans_monto
                              ) / 100
                            ).toFixed(2)}`
                          : plan.amount || "-"}
                      </TableCell>

                      <TableCell>
                        {(() => {
                          // Determinar si el plan está activo o vencido
                          let isExpired = false;
                          let statusLabel = plan.status || "N/A";

                          if (plan.expiration_date) {
                            const expirationDate = new Date(
                              plan.expiration_date
                            );
                            const currentDate = new Date();
                            isExpired = currentDate > expirationDate;

                            // Sobrescribir el estado si está vencido
                            if (isExpired) {
                              statusLabel = "Vencido";
                            } else {
                              statusLabel = "Activo";
                            }
                          }

                          // Color basado en el estado
                          const chipColor = isExpired ? "error" : "success";

                          return (
                            <Chip
                              label={statusLabel}
                              color={chipColor}
                              size="small"
                              sx={{ minWidth: "80px" }}
                            />
                          );
                        })()}
                      </TableCell>
                      <TableCell>{plan.transaction_id}</TableCell>
                      <TableCell>
                        {plan.expiration_date
                          ? new Date(plan.expiration_date).toLocaleDateString(
                              "es-ES",
                              {
                                year: "numeric",
                                month: "2-digit",
                                day: "2-digit",
                                hour: "2-digit",
                                minute: "2-digit",
                              }
                            )
                          : ""}
                      </TableCell>
                      <TableCell>
                        {plan.created_at
                          ? new Date(plan.created_at).toLocaleDateString(
                              "es-ES",
                              {
                                year: "numeric",
                                month: "2-digit",
                                day: "2-digit",
                                hour: "2-digit",
                                minute: "2-digit",
                              }
                            )
                          : ""}
                      </TableCell>
                      <TableCell>
                        {plan.updated_at
                          ? new Date(plan.updated_at).toLocaleDateString(
                              "es-ES",
                              {
                                year: "numeric",
                                month: "2-digit",
                                day: "2-digit",
                                hour: "2-digit",
                                minute: "2-digit",
                              }
                            )
                          : ""}
                      </TableCell>
                    </TableRow>
                  ));
                }

                // Si no tiene planes:
                return (
                  <TableRow key={user.id}>
                    <TableCell>
                      {user.name} {user.lastname}
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.country}</TableCell>
                    <TableCell>
                      <Chip
                        label={user.role?.name}
                        color={getPlanBadgeColor(user.role?.name)}
                      />
                    </TableCell>
                    {/* Celdas vacías o mensaje */}
                    <TableCell colSpan={7}>Sin planes</TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={11} align="center">
                  No se encontraron usuarios con el filtro seleccionado
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Paginación (opcional) */}
      <TablePagination
        component="div"
        count={totalCount}
        page={skip / limit}
        onPageChange={handleChangePage}
        rowsPerPage={limit}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50]}
        labelRowsPerPage="Usuarios por página"
      />

      <Typography variant="body2" sx={{ mt: 2, color: "gray" }}>
        Mostrando {filteredUsers.length} usuarios (con {totalCount} registros
        totales)
      </Typography>
    </Box>
  );
};

export default ListUser;
